import { useState, type ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { Loading } from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { NotAuthorized, RequireAccess } from '../auth/RequireAccess';
import { marketsForScope, type TransactionStatus } from '../dataProvider/types';
import { formatMoney } from '../cashbacks/formatMoney';
import type { TraceClick, TraceTransaction } from './trackingDiagnosticsModel';
import { useTrackingDiagnostics } from './useTrackingDiagnostics';

/**
 * Tracking diagnostics (D-tracking-diagnostics, the Phase 6 surface-3 trace). A STRICTLY
 * READ-ONLY debug trace of a member's click-to-transaction chain, so support can answer
 * "the member shopped but no cashback appeared" fast. The fraud evidence-panel idiom: a
 * buildView model assembles displayed facts, this panel renders them. There is NO action,
 * no chokepoint, no mutation anywhere.
 *
 * HONEST by construction. It shows the real spine (clicks, the click-linked transactions,
 * each transaction's current status and timestamps, the attribution fact) and labels the
 * three unmodelled links (network tracking token, vendor response, transition history) as
 * NOT RECORDED rather than fabricating them. A transaction with no originating click is
 * labelled not-click-attributed by design, not an error.
 *
 * OPERATIONAL and switcher-scoped: a member belongs to one market, so the trace is shown
 * only when the member's market is in the agent's active region scope. ROLE-gated to
 * support and super_admin.
 */

const STATUS_COLOR: Record<
  TransactionStatus,
  'default' | 'success' | 'warning' | 'error' | 'info'
> = {
  pending: 'warning',
  waiting: 'info',
  confirmed: 'success',
  rejected: 'error',
  cancelled: 'default',
};

const day = (s: string | null): string => (s ? s.slice(0, 10) : 'not set');

const Fact = ({ label, children }: { label: string; children: ReactNode }) => (
  <Box sx={{ mb: 0.5 }}>
    <Typography variant="caption" color="text.secondary">
      {label}:{' '}
    </Typography>
    <Typography variant="caption">{children}</Typography>
  </Box>
);

const Section = ({
  title,
  testId,
  children,
}: {
  title: string;
  testId: string;
  children: ReactNode;
}) => (
  <Box sx={{ mb: 3 }} data-testid={testId}>
    <Typography variant="subtitle2" sx={{ mb: 1 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const ClickLine = ({ click }: { click: TraceClick }) => (
  <Typography variant="body2" data-testid={`tracking-click-${click.id}`}>
    {click.merchantName || 'Unknown merchant'} | {click.rateSnapshot}% |{' '}
    {click.market.toUpperCase()} | clicked {day(click.occurredAt)}
    {click.converted ? '' : ' | clicked, no transaction recorded'}
  </Typography>
);

const TransactionCard = ({ txn }: { txn: TraceTransaction }) => (
  <Card
    variant="outlined"
    data-testid={`tracking-txn-${txn.id}`}
    sx={{ mb: 1.5, ...(txn.focused ? { borderColor: 'primary.main' } : {}) }}
  >
    <CardContent>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        useFlexGap
        flexWrap="wrap"
        sx={{ mb: 1 }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {formatMoney(txn.grossAmount, txn.currency)} order |{' '}
          {formatMoney(txn.cashbackAmount, txn.currency)} cashback
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          color={STATUS_COLOR[txn.status]}
          label={txn.status}
        />
        <Chip
          size="small"
          variant="outlined"
          label={`attribution: ${txn.attributionStatus}`}
          data-testid={`tracking-attr-${txn.id}`}
        />
        {txn.overdue && (
          <Chip
            size="small"
            color="warning"
            label="Overdue"
            data-testid={`tracking-overdue-${txn.id}`}
          />
        )}
        {txn.focused && <Chip size="small" color="primary" label="Focused" />}
      </Stack>

      <Fact label="Converted">{day(txn.occurredAt)}</Fact>
      <Fact label="Confirmed">{day(txn.confirmedAt)}</Fact>
      <Fact label="Expected confirmation">
        {day(txn.expectedConfirmationAt)}
      </Fact>

      {txn.originatingClick ? (
        <Fact label="Originating click">
          <span data-testid={`tracking-origin-${txn.id}`}>
            #{txn.originatingClick.id} | {txn.originatingClick.merchantName} |{' '}
            {txn.originatingClick.rateSnapshot}% | clicked{' '}
            {day(txn.originatingClick.occurredAt)}
          </span>
        </Fact>
      ) : (
        <Typography
          variant="caption"
          color="text.secondary"
          component="div"
          data-testid={`tracking-noclick-${txn.id}`}
        >
          No originating click recorded (not click attributed)
        </Typography>
      )}
    </CardContent>
  </Card>
);

/** The trace, without the access wrapper. Exported so tests can inject `asOf`. */
export const TrackingDiagnosticsView = ({ asOf }: { asOf?: number }) => {
  const { userId } = useParams();
  const [searchParams] = useSearchParams();
  const focusParam = searchParams.get('transaction');
  const focusTransactionId = focusParam ? Number(focusParam) : null;
  const [now] = useState<number>(() => asOf ?? Date.now());
  const { region } = useRegion();

  const { view, isLoading } = useTrackingDiagnostics(
    Number(userId),
    now,
    focusTransactionId,
  );

  if (isLoading) return <Loading />;
  if (!view) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary">
          No member found for this trace.
        </Typography>
      </Box>
    );
  }

  // Operational region scope: a member belongs to one market, so the trace is shown only
  // when that market is in the agent's active region scope.
  const markets = marketsForScope(region);
  if (!markets.includes(view.member.market)) return <NotAuthorized />;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1000, mx: 'auto' }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: 1 }}
      >
        Support
      </Typography>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
        Tracking diagnostics
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 1 }}
        data-testid="tracking-member"
      >
        {view.member.name} | {view.member.email} |{' '}
        {view.member.market.toUpperCase()}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        component="p"
        sx={{ mb: 3 }}
        data-testid="tracking-current-state-caveat"
      >
        This trace shows the platform-side recorded chain. Only the current
        state of each transaction is recorded. There is no transition history,
        and feed-driven confirmations are not in the audit log, so a status is a
        snapshot not a timeline.
      </Typography>

      <Divider sx={{ mb: 3 }} />

      {/* The centre: the member clicked but no transaction followed. */}
      <Section
        title={`Clicks with no transaction (${view.unconvertedClicks.length})`}
        testId="tracking-unconverted"
      >
        {view.unconvertedClicks.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            None recorded.
          </Typography>
        ) : (
          <Stack spacing={0.5}>
            {view.unconvertedClicks.map((c) => (
              <ClickLine key={c.id} click={c} />
            ))}
          </Stack>
        )}
      </Section>

      <Divider sx={{ mb: 3 }} />

      <Section
        title={`Transactions (${view.transactions.length})`}
        testId="tracking-transactions"
      >
        {view.transactions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            None recorded.
          </Typography>
        ) : (
          view.transactions.map((t) => <TransactionCard key={t.id} txn={t} />)
        )}
      </Section>

      <Divider sx={{ mb: 3 }} />

      <Section
        title={`All clicks (${view.clicks.length})`}
        testId="tracking-clicks"
      >
        {view.clicks.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            None recorded.
          </Typography>
        ) : (
          <Stack spacing={0.5}>
            {view.clicks.map((c) => (
              <ClickLine key={c.id} click={c} />
            ))}
          </Stack>
        )}
      </Section>

      <Divider sx={{ mb: 3 }} />

      {/* The honest edges: the three chain links the mock does not record. NOT fabricated. */}
      <Section title="Not recorded in this view" testId="tracking-unrecorded">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          These links of the tracking chain are not modelled here and are not
          shown as fictional values. They are recorded by the real tracking
          pipeline and will surface here once that data is available (the swap
          seam).
        </Typography>
        <Stack spacing={0.25}>
          <Typography variant="body2" color="text.secondary">
            Network tracking token (the echoed click reference): not recorded.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Vendor or network response (the raw callback): not recorded.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Normalisation record (the raw conversion before it became a
            transaction): not recorded.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            State-transition history (when a status changed): not recorded, only
            the current status is.
          </Typography>
        </Stack>
      </Section>
    </Box>
  );
};

/**
 * Gated entry registered at /tracking-diagnostics/:userId. ROLE-gated to support and
 * super_admin (a capability read-gate would let the viewer in via '*:read'), like the
 * ticket inbox.
 */
export const TrackingDiagnostics = () => (
  <RequireAccess roles={['support', 'super_admin']}>
    <TrackingDiagnosticsView />
  </RequireAccess>
);
